import { useEffect, useState } from "react";

import { findAcaoQualificacao, findTurma, updateTurma } from "../../../../API/apiAcoes";
import { useAppFeedback } from "hooks/useAppFeedback";
import { TurmaAcaoInsert } from "../../../../types/acao";

export const useTurmaSave = (turmaId?: string | null) => {
  const { showError, showSuccess } = useAppFeedback();

  const [loading, setLoading] = useState<boolean>(false);
  const [loadingSave, setLoadingSave] = useState<boolean>(false);
  const [acaoPeriodo, setAcaoPeriodo] = useState<{ data_inicio: string; data_fim: string } | null>(
    null,
  );
  const [acaoCurso, setAcaoCurso] = useState<string | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [turma, setTurma] = useState<TurmaAcaoInsert>({
    acao_id: 0,
    modalidade_id: 0,
    nome: "",
    carga_horaria: null,
    data_inicio: "",
    data_fim: "",
    data_inicio_inscricao: "",
    data_termino_inscricao: "",
    hora_inicio_inscricao: "",
    hora_termino_inscricao: "",
    hora_inicio: "",
    hora_fim: "",
    link: "",
    cep: "",
    logradouro: "",
    numero: "",
    complemento: "",
    bairro: "",
    cidade: "",
    uf: "",
    observacoes: "",
  });

  useEffect(() => {
    if (turmaId) {
      consultarTurma();
    }
  }, [turmaId]);

  const consultarTurma = async () => {
    if (!turmaId) return;

    setLoading(true);
    try {
      const result = await findTurma(turmaId);
      if (!result) {
        showError("Não foi possível encontrar a turma.");
        return;
      }

      setTurma({
        acao_id: result.acao_id,
        modalidade_id: result.modalidade_id,
        nome: result.nome,
        carga_horaria: result.carga_horaria ?? null,
        data_inicio: result.data_inicio,
        data_fim: result.data_fim,
        data_inicio_inscricao: result.data_inicio_inscricao,
        data_termino_inscricao: result.data_termino_inscricao,
        hora_inicio_inscricao: result.hora_inicio_inscricao ?? "",
        hora_termino_inscricao: result.hora_termino_inscricao ?? "",
        hora_inicio: result.hora_inicio ?? "",
        hora_fim: result.hora_fim ?? "",
        link: result.link ?? "",
        cep: result.cep ?? "",
        logradouro: result.logradouro ?? "",
        numero: result.numero ?? "",
        complemento: result.complemento ?? "",
        bairro: result.bairro ?? "",
        cidade: result.cidade ?? "",
        uf: result.uf ?? "",
        observacoes: result.observacoes ?? "",
      });

      if (result.acao_id) {
        const acao = await findAcaoQualificacao(String(result.acao_id));
        setAcaoPeriodo({
          data_inicio: acao.data_inicio,
          data_fim: acao.data_fim,
        });
        setAcaoCurso((acao as typeof acao & { curso?: string | null }).curso || acao.nome || null);
      }
    } catch (error) {
      showError("Erro ao consultar a turma.", error);
    } finally {
      setLoading(false);
    }
  };

  const isDateInRange = (date: string, start: string, end: string) => {
    if (!date || !start || !end) return true;
    return date >= start && date <= end;
  };

  const validateForm = (): boolean => {
    const validationErrors: Record<string, string> = {};

    if (!turma.nome) {
      validationErrors.nome = "Informe o nome da turma";
    }

    if (!turma.modalidade_id) {
      validationErrors.modalidade_id = "Selecione a modalidade!";
    }

    if (!turma.carga_horaria || turma.carga_horaria <= 0) {
      validationErrors.carga_horaria = "Informe a carga horária!";
    }

    if (!turma.data_inicio) {
      validationErrors.data_inicio = "Informe a data de início!";
    }

    if (!turma.data_fim) {
      validationErrors.data_fim = "Informe a data de término!";
    }

    if (!turma.data_inicio_inscricao) {
      validationErrors.data_inicio_inscricao = "Informe a data de início das inscrições!";
    }

    if (!turma.data_termino_inscricao) {
      validationErrors.data_termino_inscricao = "Informe a data de término das inscrições!";
    }

    if (!turma.hora_inicio_inscricao) {
      validationErrors.hora_inicio_inscricao = "Informe a hora de início das inscrições!";
    }

    if (!turma.hora_termino_inscricao) {
      validationErrors.hora_termino_inscricao = "Informe a hora de término das inscrições!";
    }

    if (!turma.hora_inicio) {
      validationErrors.hora_inicio = "Informe a hora de início da turma!";
    }

    if (!turma.hora_fim) {
      validationErrors.hora_fim = "Informe a hora de término da turma!";
    }

    if (acaoPeriodo) {
      if (!isDateInRange(turma.data_inicio, acaoPeriodo.data_inicio, acaoPeriodo.data_fim)) {
        validationErrors.data_inicio = "Data de início deve estar dentro do período da ação.";
      }

      if (!isDateInRange(turma.data_fim, acaoPeriodo.data_inicio, acaoPeriodo.data_fim)) {
        validationErrors.data_fim = "Data de término deve estar dentro do período da ação.";
      }
    }

    if (turma.modalidade_id == 4) {
      if (!turma.cep.trim()) {
        validationErrors.cep = "Informe o CEP!";
      }
      if (!turma.logradouro.trim()) {
        validationErrors.logradouro = "Informe o logradouro!";
      }
      if (!turma.numero.trim()) {
        validationErrors.numero = "Informe o número!";
      }
      if (!turma.bairro.trim()) {
        validationErrors.bairro = "Informe o bairro!";
      }
      if (!turma.cidade.trim()) {
        validationErrors.cidade = "Informe a cidade!";
      }
      if (!turma.uf.trim()) {
        validationErrors.uf = "Informe a UF!";
      }
    }

    setErrors(validationErrors);
    const firstError = Object.values(validationErrors)[0];
    if (firstError) {
      showError(firstError, undefined, "dialog", "Dados obrigatórios");
    }

    return Object.keys(validationErrors).length === 0;
  };

  const saveTurma = async (): Promise<boolean> => {
    if (!turmaId) {
      return false;
    }

    if (!validateForm()) {
      return false;
    }

    setLoadingSave(true);

    try {
      await updateTurma(turmaId, turma);
      showSuccess("Turma atualizada com sucesso!");
      return true;
    } catch (error) {
      showError("Não foi possível atualizar a turma.", error);
      return false;
    } finally {
      setLoadingSave(false);
    }
  };

  const handleInputChange = (value: string | number, nameObject: string, isNumber?: boolean) => {
    setTurma((currentTurma) => ({
      ...currentTurma,
      [nameObject]: isNumber ? Number(value) : value,
    }));
  };

  const handleChange = (field: keyof TurmaAcaoInsert, value: any) => {
    setTurma((currentTurma) => {
      if (field === "modalidade_id") {
        return {
          ...currentTurma,
          [field]: value,
          link: "",
          cep: "",
          logradouro: "",
          numero: "",
          complemento: "",
          bairro: "",
          cidade: "",
          uf: "",
        };
      }

      return {
        ...currentTurma,
        [field]: value,
      };
    });
  };

  return {
    turma,
    acaoCurso,
    setTurma,
    loading,
    loadingSave,
    errors,
    consultarTurma,
    saveTurma,
    handleInputChange,
    handleChange,
  };
};
